import { FilterMultiSelect, FilterSelect } from '@/components/ui/filter-select'
import { translated } from '@/lib/api/common'
import type { Translation } from '@/lib/api/common'

import { useBranches } from '../hooks'

/**
 * The least a branch has to be to appear in the picker.
 *
 * Structural rather than `TenantBranch` because three features carry their own
 * narrower ref — `StaffBranchRef`, `PosBranchRef` — off endpoints that answer
 * with an id and a name and nothing else. All of them satisfy this, so one
 * control can render any of their lists.
 */
export interface BranchOption {
  id: string
  name: Translation | null
}

interface BranchPickerProps {
  branches: readonly BranchOption[]
  value: string | null
  onChange: (branchId: string | null) => void
  /**
   * The word beside the trigger. "Branch" everywhere it is a scope the merchant
   * is choosing; the POS and Reports modules say "Outlet" because their own
   * screens talk about outlets throughout.
   */
  label?: string
  allLabel?: string
  /**
   * Clear when a branch is not optional — opening hours and an employee list
   * are always read one outlet at a time, so there is no "all" to offer.
   */
  allowAll?: boolean
  isDisabled?: boolean
  /** Wide enough for a real branch name before it truncates. */
  triggerClassName?: string
}

/**
 * The outlet picker, over a branch list the caller already holds.
 *
 * Use `BranchFilter` instead unless the list is not the tenant's whole set:
 * Staff reads `/staff/branches`, which a branch-scoped manager sees narrowed to
 * their own outlets, and Reports is handed the list its page already fetched.
 * Rendering those through this component is what keeps one scope control on
 * every module without any of them having to show branches their caller cannot
 * reach.
 *
 * **Always rendered, including for a business with one branch.** It used to hide
 * itself below two, on the grounds that a filter whose every setting produces
 * the same list is a control you have to read before you can ignore it. That
 * traded a small saving for a large surprise: a single-outlet merchant saw no
 * scope control anywhere in the product, could not tell whether a screen was
 * narrowed, and had nothing to point at when asking why — and the control
 * appeared, unannounced, on the day they opened a second outlet.
 *
 * So it states the scope instead of only offering to change it. With one branch
 * an optional filter reads "All branches" over a list of one, and a required one
 * — open hours, an employee list — names the outlet being edited, which is the
 * more useful of the two things it was hiding.
 *
 * Drawing it unconditionally also keeps the module bar still: a control that
 * appears once the branch list has loaded moves everything beside it.
 */
export function BranchPicker({
  branches,
  value,
  onChange,
  label = 'Branch',
  allLabel = 'All branches',
  allowAll = true,
  isDisabled,
  triggerClassName = 'w-44',
}: BranchPickerProps) {
  return (
    <FilterSelect
      label={label}
      allLabel={allowAll ? allLabel : undefined}
      triggerClassName={triggerClassName}
      options={branches.map((branch) => ({
        value: branch.id,
        label: translated(branch.name, 'Branch'),
      }))}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
    />
  )
}

/**
 * The outlet picker every module filters by.
 *
 * Six modules had grown their own copy of this — the same `FilterSelect`, the
 * same "All branches", and the same rule about when to show it — and the next
 * module to want one would have grown a seventh.
 *
 * The branch list is cached by `useBranches` for five minutes and shared across
 * the app, so putting this on every module costs one request rather than one
 * per screen.
 *
 * It does NOT own the filter value. A module's branch choice belongs to that
 * module — Orders keeps it beside its other filters and resets its paging with
 * it, Products hands it down to one tab — and a component that remembered the
 * choice for everybody would make changing it on one screen change another.
 */
export function BranchFilter({ isDisabled, ...props }: Omit<BranchPickerProps, 'branches'>) {
  const { branches, isLoading } = useBranches()

  return <BranchPicker {...props} branches={branches} isDisabled={isDisabled || isLoading} />
}

/* -------------------------------------------------------------------------- */
/* Several outlets at once                                                    */
/* -------------------------------------------------------------------------- */

interface BranchMultiPickerProps {
  branches: readonly BranchOption[]
  /** Chosen outlets. **Empty is every outlet**, never none — see `FilterMultiSelect`. */
  value: readonly string[]
  onChange: (branchIds: string[]) => void
  label?: string
  allLabel?: string
  isDisabled?: boolean
  triggerClassName?: string
}

/**
 * The outlet picker for the lists that can show SEVERAL outlets together, over
 * a branch list the caller already holds.
 *
 * The single-select `BranchPicker` is still the right control wherever a screen
 * is inherently about one room: a QR sheet is printed per room, a floor grid is
 * one room's tables, and an approval policy is one outlet's rules. This one is
 * for the lists — orders, customers, drawers, exceptions — where a manager of
 * three outlets wants the three of them in one list, ranked against each other,
 * rather than the same screen read three times and added up by hand.
 *
 * **Always rendered, including for a single-outlet business.** The single-select
 * `BranchPicker` hides itself below two branches, on the grounds that a filter
 * whose every setting produces the same list is a control you have to read
 * before you can ignore it. That reasoning does not carry over here, for two
 * reasons:
 *
 *   · It is the module's standing statement of scope, not only a way to narrow.
 *     "Location: All locations" over a list is the screen saying what it covers,
 *     and a merchant who opens a second outlet tomorrow should not have the
 *     meaning of every list they have learnt change silently under them.
 *   · Rendering nothing until the branch list has loaded, then appearing, moves
 *     every control beside it. Always drawing it — disabled until there is
 *     something in it — keeps the module bar still.
 *
 * With one outlet it is therefore a dropdown holding that one outlet, and with
 * none it is a disabled dropdown reading "All locations", which is the truth.
 */
export function BranchMultiPicker({
  branches,
  value,
  onChange,
  label = 'Location',
  allLabel = 'All locations',
  isDisabled,
  triggerClassName = 'w-44',
}: BranchMultiPickerProps) {
  return (
    <FilterMultiSelect
      label={label}
      allLabel={allLabel}
      // "2 locations", not "2 selected". The label is three words away at the
      // left of the control and a count on its own reads as a tally of
      // something else once the row has other filters on it.
      summaryLabel={(count) => `${count} locations`}
      triggerClassName={triggerClassName}
      options={branches.map((branch) => ({
        value: branch.id,
        label: translated(branch.name, 'Branch'),
      }))}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
    />
  )
}

/**
 * The multi-outlet picker every list module filters by, over the tenant's whole
 * branch list.
 *
 * `BranchMultiPicker`'s relationship to this is exactly `BranchPicker`'s to
 * `BranchFilter`: use this unless the list the caller can reach is narrower than
 * the tenant's whole set, as Staff's is and as Reports' already-fetched one is.
 *
 * Shares `useBranches` with every other picker in the app, so adding it to a
 * module costs no extra request.
 *
 * It does NOT own the filter value, for the same reason the single one does not:
 * a module's outlet choice belongs to that module, and remembering it centrally
 * would make changing it on Orders change it on Customers.
 */
export function BranchMultiFilter({
  isDisabled,
  ...props
}: Omit<BranchMultiPickerProps, 'branches'>) {
  const { branches, isLoading } = useBranches()

  return <BranchMultiPicker {...props} branches={branches} isDisabled={isDisabled || isLoading} />
}
